import { Mic2, Clapperboard, Camera, Radio } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading, useDocTitle } from "../components/Reveal";
import { MediaImg } from "../components/MediaImg";
import { CTABanner } from "../components/CTABanner";
import { GoldLink } from "../components/Buttons";
import { IMAGES } from "../data/site";

const OFFERS = [
  {
    icon: Mic2,
    num: "01",
    title: "Podcast Studios",
    copy: "Broadcast-quality rooms that make hitting record the easiest part of the show.",
    points: ["Acoustic treatment & tuning", "Microphone & mixer chains", "One-button recording", "Video-podcast ready"],
  },
  {
    icon: Clapperboard,
    num: "02",
    title: "Video & Interview Studios",
    copy: "Controlled spaces for interviews, courses and content — lit once, correctly, forever.",
    points: ["Permanent lighting plots", "Multi-cam PTZ rigs", "Teleprompter & monitoring", "Backdrop systems"],
  },
  {
    icon: Camera,
    num: "03",
    title: "Content Creation Rooms",
    copy: "A dedicated room that turns 'we should post more' into a Tuesday afternoon.",
    points: ["Vertical video setups", "Product photo corners", "Key & fill lighting", "Storage & workflow"],
  },
  {
    icon: Radio,
    num: "04",
    title: "Livestream Spaces",
    copy: "Rooms built to broadcast — to members, customers, or the whole internet.",
    points: ["Switching & encoding", "Redundant internet paths", "Audio for stream + room", "Graphics & lower thirds"],
  },
];

const STEPS = [
  ["Design", "Room layout, acoustics, lighting plots and sightlines — planned around what you'll actually create."],
  ["Equipment", "Selection driven by your content and budget, not by what's on sale. Buy once, buy right."],
  ["Installation", "Clean, labeled, serviceable. Installed like we'll be the ones maintaining it — because often we are."],
  ["Configuration", "Scenes, presets and one-button workflows so the room is easy on its worst day, not just its best."],
  ["Training", "We train your team, leave documentation, and stay a phone call away."],
];

export default function Systems() {
  useDocTitle("Studio Builds");
  return (
    <div data-testid="systems-page">
      <PageHero
        eyebrow="Studio Builds & Integration"
        lines={["We don't just create content.", <span key="x" className="text-gold-dark">We build the places where content gets created.</span>]}
        sub="Podcast studios, video studios, content rooms and livestream spaces — designed, installed and configured for businesses, churches, creators and organizations."
      >
        <div className="mt-9 flex flex-wrap gap-4">
          <GoldLink to="/start-a-project" testId="systems-hero-cta">Plan My Studio</GoldLink>
        </div>
      </PageHero>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-28 lg:px-16">
        <SectionHeading
          eyebrow="What We Build"
          title={<>Rooms that work <span className="text-gold-dark">as hard as you do.</span></>}
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {OFFERS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.07}>
              <div
                data-testid={`systems-offer-${o.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "")}`}
                className="group h-full border border-ink/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-xl sm:p-10"
              >
                <div className="flex items-center justify-between">
                  <o.icon size={28} className="text-gold-dark" />
                  <span className="font-mono text-[10px] tracking-[0.3em] text-ink/30">{o.num}</span>
                </div>
                <h3 className="mt-7 font-display text-2xl font-bold tracking-tight text-ink">{o.title}</h3>
                <p className="mt-3.5 text-sm leading-relaxed text-smoke">{o.copy}</p>
                <ul className="mt-6 space-y-2.5 border-t border-ink/10 pt-6">
                  {o.points.map((pt) => (
                    <li key={pt} className="flex gap-3 font-mono text-[11px] uppercase tracking-[0.15em] text-ink/55">
                      <span className="text-gold">◆</span>
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">
            Audio systems · Lighting systems · Camera systems · Equipment selection · Consulting
          </p>
        </Reveal>
      </section>

      <section className="bg-cream">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 sm:py-28 lg:grid-cols-2 lg:px-16">
          <Reveal>
            <div className="clip-frame overflow-hidden shadow-2xl">
              <MediaImg src="/assets/media/studio-a.jpg" alt="Podcast studio built by Studio Solutions" className="h-[420px] w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionHeading
              eyebrow="How a Build Works"
              title={<>Design to training, <span className="text-gold-dark">one plan.</span></>}
              sub="Five steps, one point of contact, and a room your least technical teammate can run."
            />
            <Reveal delay={0.15}>
              <ol className="mt-9 space-y-5">
                {STEPS.map(([title, copy], i) => (
                  <li key={title} className="flex gap-5">
                    <span className="font-mono text-sm tracking-[0.2em] text-gold-dark">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="font-display text-base font-bold text-ink">{title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-smoke">{copy}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABanner
        title="Stop fighting the room."
        sub="Send us the floor plan and the frustration. We'll send back a plan with phases, prices, and a payoff."
      />
    </div>
  );
}
